import { io, Socket } from 'socket.io-client';

const LOG_PREFIX = '[AdminPanel WebSocket]';

export type SocketDebugInfo = {
	apiUrl: string;
	socketPort: string;
	mode: 'same-origin' | 'explicit-port';
	connectUrl: string;
	socketIoPath: string;
	transports: string;
	verboseDebug: boolean;
	devtoolsHint: string;
};

function usesSameOrigin(apiUrl: string, socketPort: string): boolean {
	return apiUrl.startsWith('https://') || !socketPort || socketPort === 'same-origin' || socketPort === '443';
}

function isVerboseDebug(): boolean {
	return process.env.REACT_APP_SOCKET_DEBUG === 'true';
}

/** HTTPS / nginx: same origin (443 → /socket.io/ → :8081). HTTP dev: explicit port e.g. 8081. */
function resolveSocketUrl(): string {
	const apiUrl = process.env.REACT_APP_API_URL ?? '';
	const socketPort = (process.env.REACT_APP_SOCKET_PORT ?? '').trim();
	if (usesSameOrigin(apiUrl, socketPort)) {
		return apiUrl;
	}
	return `${apiUrl}:${socketPort}`;
}

function resolveTransports(apiUrl: string, socketPort: string): ('websocket' | 'polling')[] {
	// HTTPS via nginx: WebSocket-only → one clear WSS row in DevTools → Network → WS (Messages tab).
	if (usesSameOrigin(apiUrl, socketPort) || isVerboseDebug()) {
		return ['websocket'];
	}
	return ['polling', 'websocket'];
}

export function getSocketDebugInfo(): SocketDebugInfo {
	const apiUrl = process.env.REACT_APP_API_URL ?? '';
	const socketPort = (process.env.REACT_APP_SOCKET_PORT ?? '').trim();
	const sameOrigin = usesSameOrigin(apiUrl, socketPort);
	const transports = resolveTransports(apiUrl, socketPort);

	return {
		apiUrl: apiUrl || '(unset)',
		socketPort: socketPort || '(unset)',
		mode: sameOrigin ? 'same-origin' : 'explicit-port',
		connectUrl: resolveSocketUrl(),
		socketIoPath: '/socket.io/',
		transports: transports.join(', '),
		verboseDebug: isVerboseDebug(),
		devtoolsHint:
			'DevTools → Network → filter WS → select socket.io → Messages (WSS over HTTPS, same as WS over HTTP)',
	};
}

function enableSocketIoEngineDebug(): void {
	if (typeof localStorage === 'undefined' || !isVerboseDebug()) {
		return;
	}
	localStorage.setItem('debug', 'socket.io-client:*,engine.io-client:*');
}

function logSocketConfig(): void {
	console.info(`${LOG_PREFIX} config`, getSocketDebugInfo());
}

const URL = resolveSocketUrl();
const apiUrlForOptions = process.env.REACT_APP_API_URL ?? '';
const socketPortForOptions = (process.env.REACT_APP_SOCKET_PORT ?? '').trim();

class SocketSingleton {
	private static instance: Socket;
	private static configLogged = false;

	private constructor() {}

	public static getInstance(): Socket {
		if (!SocketSingleton.instance) {
			enableSocketIoEngineDebug();

			if (!SocketSingleton.configLogged) {
				logSocketConfig();
				SocketSingleton.configLogged = true;
			}

			if (!URL) {
				console.error(`${LOG_PREFIX} missing REACT_APP_API_URL — socket.io will not connect`);
			}

			SocketSingleton.instance = io(URL, {
				path: '/socket.io/',
				transports: resolveTransports(apiUrlForOptions, socketPortForOptions),
			});

			SocketSingleton.instance.on('connect', () => {
				const transport = SocketSingleton.instance.io.engine?.transport?.name;
				console.info(`${LOG_PREFIX} connected`, {
					id: SocketSingleton.instance.id,
					url: URL,
					transport,
					secure: URL.startsWith('https://'),
				});
			});

			SocketSingleton.instance.on('disconnect', (reason) => {
				console.warn(`${LOG_PREFIX} disconnected`, { reason, url: URL });
			});

			SocketSingleton.instance.on('reconnect', (attempt) => {
				console.info(`${LOG_PREFIX} reconnected`, { attempt, url: URL, id: SocketSingleton.instance.id });
			});

			SocketSingleton.instance.io.on('reconnect_attempt', (attempt) => {
				console.debug(`${LOG_PREFIX} reconnect_attempt`, { attempt, url: URL });
			});

			SocketSingleton.instance.io.on('reconnect_error', (error) => {
				console.error(`${LOG_PREFIX} reconnect_error`, error.message, getSocketDebugInfo());
			});

			SocketSingleton.instance.on('connect_error', (error) => {
				console.error(`${LOG_PREFIX} connect_error`, error.message, getSocketDebugInfo());
			});
		}
		return SocketSingleton.instance;
	}
}

export const socket = SocketSingleton.getInstance();

if (typeof window !== 'undefined') {
	(window as Window & { __adminPanelSocketDebug?: () => SocketDebugInfo }).__adminPanelSocketDebug =
		getSocketDebugInfo;
}
